/**
 * `<xa-stacked-bar>` — vertical stacked bar chart (default variant A
 * "classic flat"). Registered as `xaStackedBar`.
 *
 * Bindings:
 * | Binding | Type | Default | Notes |
 * |---|---|---|---|
 * | `data` | `XaCategorical` | — | required |
 * | `colors` | `XaPalette` | `'tokens'` | palette name or hex array; global `xaChartsConfig.palette` wins over the default |
 * | `width` | `number` | `520` | SVG px; ignored when `auto-width` is on |
 * | `auto-width` | `boolean` | `false` | re-flow to the container width (responsive) |
 * | `height` | `number` | `280` | SVG px |
 * | `variant` | `'flat' \| 'rounded'` | `'flat'` | rounded = 2px segment gaps + capped top |
 * | `legend` | `'top' \| 'bottom' \| 'none'` | `'bottom'` | bottom uses column layout |
 * | `legend-values` | `boolean` | `true` | per-series totals in legend |
 * | `max-x-labels` | `number` | `8` | x-label thinning |
 * | `on-series-toggle` | `&` callback | — | `$event: XaSeriesToggleEvent` |
 * | `on-category-hover` | `&` callback | — | `$event: XaHoverEvent` |
 */
import {
  categoricalAriaLabel,
  computeStackedBar,
  ensureChartStyles,
  legendLayoutFor,
  normalizeCategorical,
  stackedBarTooltip,
} from '@xait/charts-core';
import type {
  XaBarGroup,
  XaCategorical,
  XaHoverEvent,
  XaLegendLayout,
  XaLegendPos,
  XaPalette,
  XaSeriesToggleEvent,
  XaStackedBarLayout,
} from '@xait/charts-core';
import { XaChartBaseController, XAC_CATEGORICAL_TABLE, XAC_TIP_TEMPLATE } from './chart-base';
import type { XaCallback } from './chart-base';
import type { XaChartsDefaults } from './config';

class XaStackedBarController extends XaChartBaseController {
  static override $inject = ['$element', '$scope', 'xaChartsConfig'];

  /* bindings */
  data?: XaCategorical;
  colors?: XaPalette;
  width?: number;
  height?: number;
  variant?: 'flat' | 'rounded';
  legend?: XaLegendPos;
  legendValues?: boolean;
  maxXLabels?: number;
  rotateXLabels?: boolean;
  onSeriesToggle!: XaCallback<XaSeriesToggleEvent>;
  onCategoryHover!: XaCallback<XaHoverEvent>;

  /* view state */
  layout: XaStackedBarLayout | null = null;
  legendPos: XaLegendPos = 'bottom';
  legendLayout: XaLegendLayout = 'column';
  legendValuesOn = true;
  ariaLabel = '';

  private readonly hidden = new Set<string>();
  private hoverIndex: number | null = null;

  constructor(
    $element: ng.IAugmentedJQuery,
    private readonly $scope: ng.IScope,
    private readonly xaChartsConfig: XaChartsDefaults,
  ) {
    super($element);
  }

  $onInit(): void {
    ensureChartStyles();
    this.recompute();
  }

  $onChanges(): void {
    this.recompute();
  }

  override $postLink(): void {
    super.$postLink();
    this.observeAutoWidth(() => this.$scope.$applyAsync(() => this.recompute()));
  }

  $onDestroy(): void {
    this.disconnectAutoWidth();
  }

  toggleSeries(name: string): void {
    const nowHidden = !this.hidden.delete(name);
    if (nowHidden) this.hidden.add(name);
    this.recompute();
    this.onSeriesToggle({ $event: { name, hidden: nowHidden, hiddenAll: Array.from(this.hidden) } });
  }

  isolateSeries(name: string): void {
    const all = (this.layout?.legend ?? []).map((it) => it.label);
    const isolated =
      all.length > 1 && !this.hidden.has(name) && all.every((n) => n === name || this.hidden.has(n));
    this.hidden.clear();
    if (!isolated) {
      for (const n of all) if (n !== name) this.hidden.add(n);
    }
    this.recompute();
    this.onSeriesToggle({
      $event: { name, hidden: this.hidden.has(name), hiddenAll: Array.from(this.hidden) },
    });
  }

  hoverGroup(ev: MouseEvent, group: XaBarGroup): void {
    if (!this.layout) return;
    this.showTip(ev, stackedBarTooltip(this.layout, group.index));
    if (this.hoverIndex !== group.index) {
      this.hoverIndex = group.index;
      this.onCategoryHover({ $event: { categoryIndex: group.index, category: group.category } });
    }
  }

  leaveGroups(): void {
    this.hideTip();
    if (this.hoverIndex !== null) {
      this.hoverIndex = null;
      this.onCategoryHover({ $event: { categoryIndex: null } });
    }
  }

  private recompute(): void {
    if (this.data) {
      this.data = normalizeCategorical(this.data);
    }
    this.legendPos = this.legend ?? 'bottom';
    this.legendValuesOn = this.legendValues ?? true;
    this.legendLayout = legendLayoutFor('stacked-bar', this.legendPos, this.legendValuesOn);
    if (!this.data) {
      this.layout = null;
      return;
    }
    this.layout = computeStackedBar(this.data, {
      width: this.effectiveWidth(this.width ?? 520),
      height: this.height ?? 280,
      variant: this.variant ?? 'flat',
      maxXLabels: this.maxXLabels ?? 8,
      rotateXLabels: this.rotateXLabels ?? false,
      colors: this.colors ?? this.xaChartsConfig.palette ?? 'tokens',
      hidden: this.hidden,
    });
    this.ariaLabel = categoricalAriaLabel('Stacked bar', this.data);
  }
}

export const xaStackedBarComponent: ng.IComponentOptions = {
  controller: XaStackedBarController,
  bindings: {
    data: '<',
    colors: '<',
    width: '<',
    autoWidth: '<',
    height: '<',
    variant: '<',
    legend: '<',
    legendValues: '<',
    maxXLabels: '<',
    rotateXLabels: '<',
    onSeriesToggle: '&',
    onCategoryHover: '&',
  },
  template: `<div class="xac-chart">
  <xac-legend ng-if="$ctrl.layout && $ctrl.legendPos === 'top'" items="$ctrl.layout.legend" layout="$ctrl.legendLayout" show-values="$ctrl.legendValuesOn" on-toggle="$ctrl.toggleSeries(label)" on-isolate="$ctrl.isolateSeries(label)"></xac-legend>
  <svg ng-if="$ctrl.layout" class="xac-svg" role="img" aria-label="{{$ctrl.ariaLabel}}" ng-attr-width="{{$ctrl.layout.width}}" ng-attr-height="{{$ctrl.layout.height}}" overflow="visible">
    <g ng-repeat="g in $ctrl.layout.grid track by $index">
      <line ng-attr-x1="{{g.x0}}" ng-attr-x2="{{g.x1}}" ng-attr-y1="{{g.y}}" ng-attr-y2="{{g.y}}" ng-attr-stroke="{{g.stroke}}"></line>
      <text class="xac-axis" text-anchor="end" ng-attr-x="{{g.labelX}}" ng-attr-y="{{g.labelY}}">{{g.label}}</text>
    </g>
    <g class="xac-bargroup" ng-repeat="grp in $ctrl.layout.groups track by $index" ng-mousemove="$ctrl.hoverGroup($event, grp)" ng-mouseleave="$ctrl.leaveGroups()">
      <rect fill="transparent" ng-attr-x="{{grp.hitX}}" ng-attr-y="{{grp.hitY}}" ng-attr-width="{{grp.hitWidth}}" ng-attr-height="{{grp.hitHeight}}"></rect>
      <rect ng-repeat="seg in grp.segments track by seg.seriesName" ng-attr-x="{{seg.x}}" ng-attr-y="{{seg.y}}" ng-attr-width="{{seg.width}}" ng-attr-height="{{seg.height}}" ng-attr-rx="{{seg.rx}}" ng-attr-fill="{{seg.color}}"></rect>
      <text ng-if="grp.label" class="xac-axis" ng-attr-text-anchor="{{grp.label.rotate ? 'end' : 'middle'}}" ng-attr-x="{{grp.label.x}}" ng-attr-y="{{grp.label.y}}" ng-attr-transform="{{grp.label.rotate ? 'rotate(' + grp.label.rotate + ' ' + grp.label.x + ' ' + grp.label.y + ')' : undefined}}">{{grp.label.text}}</text>
    </g>
  </svg>
  <xac-legend ng-if="$ctrl.layout && $ctrl.legendPos === 'bottom'" items="$ctrl.layout.legend" layout="$ctrl.legendLayout" show-values="$ctrl.legendValuesOn" on-toggle="$ctrl.toggleSeries(label)" on-isolate="$ctrl.isolateSeries(label)"></xac-legend>
  ${XAC_CATEGORICAL_TABLE}
  ${XAC_TIP_TEMPLATE}
</div>`,
};
